import { z } from 'zod';
import {
  billListResponseSchema,
  billSchema,
  cashShiftListResponseSchema,
  cashShiftSchema,
  currentCashShiftResponseSchema,
  paymentResultSchema,
} from './billing';
import {
  catalogResponseSchema,
  kitchenStationSchema,
  menuCategorySchema,
  menuItemSchema,
  modifierGroupSchema,
} from './catalog';
import { apiErrorSchema, type ApiErrorBody } from './errors';
import {
  branchListResponseSchema,
  branchSchema,
  meResponseSchema,
  type CreateBranchRequest,
  type SignInRequest,
} from './iam';
import { kitchenTicketListResponseSchema, kitchenTicketSchema } from './kitchen';
import { orderListResponseSchema, orderSchema } from './orders';
import {
  stockCountListResponseSchema,
  stockCountPostResultSchema,
  stockCountSchema,
  countAccuracyResponseSchema,
} from './counts';
import {
  expiryAlertResponseSchema,
  fefoResponseSchema,
  inventoryItemListResponseSchema,
  inventoryItemSchema,
  lotDetailSchema,
  lotListResponseSchema,
  lotSchema,
  postingResultSchema,
  stockLocationListResponseSchema,
  stockLocationSchema,
  stockMovementPageSchema,
  stockResponseSchema,
  valuationHistoryResponseSchema,
  valuationResponseSchema,
} from './inventory';
import {
  goodsReceiptListResponseSchema,
  goodsReceiptPostResultSchema,
  goodsReceiptSchema,
  purchaseOrderListResponseSchema,
  purchaseOrderSchema,
  purchaseRequestListResponseSchema,
  purchaseRequestSchema,
  supplierListResponseSchema,
  supplierSchema,
} from './procurement';
import {
  approvalDelegationListResponseSchema,
  approvalDelegationSchema,
  approvalPolicyListResponseSchema,
  approvalPolicySchema,
  approvalRequestListResponseSchema,
  approvalRequestSchema,
  inboxResponseSchema,
} from './approvals';
import { budgetEntriesResponseSchema, budgetListResponseSchema, budgetSchema } from './budgets';
import {
  agingResponseSchema,
  apPaymentListResponseSchema,
  apPaymentSchema,
  apSettingsSchema,
  creditNoteListResponseSchema,
  creditNoteSchema,
  invoiceListResponseSchema,
  invoiceSchema,
} from './ap';
import {
  stockTransferListResponseSchema,
  stockTransferPostResultSchema,
  stockTransferSchema,
} from './transfers';
import {
  consumptionReportSchema,
  consumptionRunResultSchema,
  recipeListResponseSchema,
  recipeSchema,
} from './recipes';
import {
  priceHistoryResponseSchema,
  scorecardSchema,
  supplierCompareResponseSchema,
} from './supplier-analytics';
import {
  recallCaseListResponseSchema,
  recallCaseSchema,
  recallSearchResponseSchema,
} from './recalls';
import { salesReportSchema } from './reports';
import { brandingSchema, type updateBrandingRequestSchema } from './branding';
import {
  diningTableSchema,
  floorPlanResponseSchema,
  floorSchema,
  tableQrCodeListResponseSchema,
  tableQrCodeSchema,
  tableSessionSchema,
} from './restaurant';
import {
  guestBillSchema,
  guestMenuSchema,
  guestOrderResponseSchema,
  guestOnlinePaymentSchema,
  simulatorCheckoutSchema,
} from './guest';
import { promotionListResponseSchema, promotionSchema } from './promotions';
import { createStaffResponseSchema, staffListResponseSchema, staffMemberSchema } from './staff';
import {
  accountSecuritySchema,
  acceptedResponseSchema,
  emailVerificationResponseSchema,
  invitationInfoSchema,
  totpSetupResponseSchema,
  type AcceptInvitationRequest,
} from './account';
import {
  wasteListResponseSchema,
  wastePostResultSchema,
  wasteRecordSchema,
  wasteReportSchema,
} from './waste';
import type * as B from './billing';
import type * as C from './catalog';
import type * as K from './kitchen';
import type * as O from './orders';
import type * as R from './restaurant';
import type * as S from './staff';
import type * as Rep from './reports';
import type * as Inv from './inventory';
import type * as Proc from './procurement';
import type * as W from './waste';
import type * as Cnt from './counts';
import type * as Appr from './approvals';
import type * as Bud from './budgets';
import type * as Ap from './ap';
import type * as Tr from './transfers';
import type * as Rcp from './recipes';
import type * as SA from './supplier-analytics';
import type * as Rcl from './recalls';

/** Tenant members someone may delegate to or escalate to (id + name). */
const memberListResponseSchema = z.object({
  items: z.array(z.object({ user_id: z.string(), display_name: z.string() })),
});

/** `GET …/inventory/reconciliation` (P1.8): projection checks against the ledgers. */
export const reconciliationReportSchema = z.object({
  branch_id: z.string(),
  checked_at: z.string(),
  checks: z.record(z.string(), z.number()),
  issues: z.array(
    z.object({
      check: z.string(),
      entity_id: z.string(),
      expected: z.number(),
      actual: z.number(),
      detail: z.string(),
    }),
  ),
  consumption_backlog: z.number(),
});
export type ReconciliationReport = z.infer<typeof reconciliationReportSchema>;

export const supplierRatingSchema = z.object({
  id: z.string(),
  period: z.string(),
  score: z.number().int(),
  comment: z.string(),
  evidence: z.string().nullable(),
  rated_by_name: z.string().nullable(),
  created_at: z.string(),
});

type QueryOf<T extends z.ZodType> = Partial<Record<keyof z.input<T>, string>>;
import type * as G from './guest';
import type * as P from './promotions';
import * as Pf from './platform';
import * as DL from './data-lifecycle';
import * as Sub from './subscription';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.requestId = body.request_id;
    this.details = body.details;
  }
}

export interface ApiClientOptions {
  /** `''` for same-origin (the default deployment: UI and API share one origin). */
  baseUrl: string;
  fetch?: typeof fetch;
}

type In<T extends z.ZodType> = z.input<T>;

const newKey = () => crypto.randomUUID();

/**
 * Typed first-party API client. Cookie credentials; mutating requests send
 * `X-Requested-With` (CSRF guard). Every POST carries an `Idempotency-Key`: pass the same
 * key when retrying the same user intent so the server can deduplicate (spec §2).
 */
export function createApiClient({ baseUrl, fetch: f = fetch }: ApiClientOptions) {
  async function request<T extends z.ZodType>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    schema: T,
    init: {
      body?: unknown;
      idempotencyKey?: string;
      query?: Record<string, string | undefined>;
    } = {},
  ): Promise<z.infer<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') headers['X-Requested-With'] = 'apis-client';
    if (method === 'POST') headers['Idempotency-Key'] = init.idempotencyKey ?? newKey();

    const qs = init.query
      ? new URLSearchParams(
          Object.entries(init.query).filter((e): e is [string, string] => e[1] !== undefined),
        ).toString()
      : '';
    // Built as a plain object: `credentials` is browser-only and absent from Workers'
    // RequestInit type, but this package is type-checked under both environments.
    const requestInit: Record<string, unknown> = {
      method,
      headers,
      credentials: 'include',
      ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
    };
    const res = await f(`${baseUrl}${path}${qs ? `?${qs}` : ''}`, requestInit as RequestInit);
    const json: unknown = res.status === 204 ? null : await res.json().catch(() => null);
    if (!res.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      throw new ApiError(
        res.status,
        parsed.success
          ? parsed.data.error
          : { code: 'INTERNAL_ERROR', message: res.statusText, request_id: '', details: {} },
      );
    }
    return schema.parse(json);
  }

  /**
   * Multipart upload (POST). The browser sets the multipart `Content-Type` with its boundary;
   * CSRF and idempotency headers are the same as for JSON commands.
   */
  async function upload<T extends z.ZodType>(
    path: string,
    schema: T,
    form: FormData,
    idempotencyKey?: string,
  ): Promise<z.infer<T>> {
    const requestInit: Record<string, unknown> = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'apis-client',
        'Idempotency-Key': idempotencyKey ?? newKey(),
      },
      credentials: 'include',
      body: form,
    };
    const res = await f(`${baseUrl}${path}`, requestInit as RequestInit);
    const json: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      throw new ApiError(
        res.status,
        parsed.success
          ? parsed.data.error
          : { code: 'INTERNAL_ERROR', message: res.statusText, request_id: '', details: {} },
      );
    }
    return schema.parse(json);
  }

  const e = encodeURIComponent;
  const br = (branchId: string) => `/v1/branches/${e(branchId)}`;

  return {
    // ---- Auth & tenancy
    signIn: (body: SignInRequest, idempotencyKey?: string) =>
      request('POST', '/v1/auth/sign-in', meResponseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    signOut: () => request('POST', '/v1/auth/sign-out', z.null()),
    switchTenant: (tenantId: string) =>
      request('POST', '/v1/auth/switch-tenant', meResponseSchema, {
        body: { tenant_id: tenantId },
      }),
    me: () => request('GET', '/v1/me', meResponseSchema),
    listBranches: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/branches`, branchListResponseSchema),
    getBranch: (branchId: string) => request('GET', br(branchId), branchSchema),
    createBranch: (tenantId: string, body: CreateBranchRequest, idempotencyKey?: string) =>
      request('POST', `/v1/tenants/${e(tenantId)}/branches`, branchSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateBranchSettings: (
      branchId: string,
      body: In<typeof S.updateBranchSettingsRequestSchema>,
    ) => request('PATCH', br(branchId), branchSchema, { body }),

    // ---- Staff
    listStaff: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/staff`, staffListResponseSchema),
    createStaff: (
      tenantId: string,
      body: In<typeof S.createStaffRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/tenants/${e(tenantId)}/staff`, createStaffResponseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    grantStaffRole: (
      tenantId: string,
      membershipId: string,
      body: In<typeof S.grantStaffRoleRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `/v1/tenants/${e(tenantId)}/staff/${e(membershipId)}/roles`,
        staffMemberSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    revokeStaffRole: (tenantId: string, membershipId: string, assignmentId: string) =>
      request(
        'DELETE',
        `/v1/tenants/${e(tenantId)}/staff/${e(membershipId)}/roles/${e(assignmentId)}`,
        staffMemberSchema,
      ),

    // ---- Account lifecycle (ADR-0031); token endpoints are public
    getInvitation: (token: string) =>
      request('GET', `/v1/account/invitations/${e(token)}`, invitationInfoSchema),
    acceptInvitation: (token: string, body: AcceptInvitationRequest) =>
      request('POST', `/v1/account/invitations/${e(token)}/accept`, meResponseSchema, { body }),
    requestPasswordReset: (email: string) =>
      request('POST', '/v1/account/password-reset', acceptedResponseSchema, { body: { email } }),
    resetPassword: (token: string, password: string) =>
      request('POST', `/v1/account/password-reset/${e(token)}`, z.null(), {
        body: { password },
      }),
    getAccountSecurity: () => request('GET', '/v1/account', accountSecuritySchema),
    sendEmailVerification: () =>
      request('POST', '/v1/account/email-verification', emailVerificationResponseSchema, {
        body: {},
      }),
    verifyEmail: (token: string) =>
      request(
        'POST',
        `/v1/account/email-verification/${e(token)}`,
        emailVerificationResponseSchema,
        { body: {} },
      ),
    setupTotp: () =>
      request('POST', '/v1/account/mfa/totp/setup', totpSetupResponseSchema, { body: {} }),
    confirmTotp: (secret: string, code: string) =>
      request('POST', '/v1/account/mfa/totp/confirm', accountSecuritySchema, {
        body: { secret, code },
      }),
    disableTotp: (code: string) =>
      request('POST', '/v1/account/mfa/totp/disable', accountSecuritySchema, { body: { code } }),

    // ---- Branding & custom domain (ADR-0036)
    getBranding: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/branding`, brandingSchema),
    updateBranding: (
      tenantId: string,
      body: In<typeof updateBrandingRequestSchema>,
      idempotencyKey: string,
    ) =>
      request('PUT', `/v1/tenants/${e(tenantId)}/branding`, brandingSchema, {
        body,
        idempotencyKey,
      }),
    setCustomDomain: (tenantId: string, hostname: string, idempotencyKey: string) =>
      request('PUT', `/v1/tenants/${e(tenantId)}/branding/custom-domain`, brandingSchema, {
        body: { hostname },
        idempotencyKey,
      }),
    checkCustomDomain: (tenantId: string) =>
      request('POST', `/v1/tenants/${e(tenantId)}/branding/custom-domain/check`, brandingSchema),
    removeCustomDomain: (tenantId: string) =>
      request('DELETE', `/v1/tenants/${e(tenantId)}/branding/custom-domain`, brandingSchema),

    // ---- Floors, tables, sessions
    getFloorPlan: (branchId: string) =>
      request('GET', `${br(branchId)}/floors`, floorPlanResponseSchema),
    createFloor: (
      branchId: string,
      body: In<typeof R.createFloorRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/floors`, floorSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateFloor: (branchId: string, floorId: string, body: In<typeof R.updateFloorRequestSchema>) =>
      request('PATCH', `${br(branchId)}/floors/${e(floorId)}`, floorSchema, { body }),
    createTable: (
      branchId: string,
      body: In<typeof R.createTableRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/tables`, diningTableSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateTable: (branchId: string, tableId: string, body: In<typeof R.updateTableRequestSchema>) =>
      request('PATCH', `${br(branchId)}/tables/${e(tableId)}`, diningTableSchema, { body }),
    /** cleaning → available, after the table has been cleared. */
    releaseTable: (branchId: string, tableId: string, idempotencyKey?: string) =>
      request('POST', `${br(branchId)}/tables/${e(tableId)}/release`, diningTableSchema, {
        ...(idempotencyKey && { idempotencyKey }),
      }),
    openTableSession: (
      branchId: string,
      body: In<typeof R.openTableSessionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/table-sessions`, tableSessionSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getTableSession: (branchId: string, sessionId: string) =>
      request('GET', `${br(branchId)}/table-sessions/${e(sessionId)}`, tableSessionSchema),
    listTableQrCodes: (branchId: string) =>
      request('GET', `${br(branchId)}/tables/qr-codes`, tableQrCodeListResponseSchema),
    /** New token: every printed QR code of this table stops working. */
    rotateTableQrCode: (branchId: string, tableId: string, idempotencyKey?: string) =>
      request('POST', `${br(branchId)}/tables/${e(tableId)}/qr-code/rotate`, tableQrCodeSchema, {
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Promotions (guest menu)
    listPromotions: (branchId: string) =>
      request('GET', `${br(branchId)}/promotions`, promotionListResponseSchema),
    createPromotion: (
      branchId: string,
      body: In<typeof P.createPromotionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/promotions`, promotionSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updatePromotion: (
      branchId: string,
      id: string,
      body: In<typeof P.updatePromotionRequestSchema>,
    ) => request('PATCH', `${br(branchId)}/promotions/${e(id)}`, promotionSchema, { body }),

    // ---- Guest QR menu (public; scoped by the table token)
    getGuestMenu: (token: string) =>
      request('GET', `/v1/guest/tables/${e(token)}`, guestMenuSchema),
    getGuestBill: (token: string) =>
      request('GET', `/v1/guest/tables/${e(token)}/bill`, guestBillSchema),
    /** The table's current order (sent items + kitchen progress), or `{ order: null }`. */
    getGuestOrder: (token: string) =>
      request('GET', `/v1/guest/tables/${e(token)}/order`, guestOrderResponseSchema),
    /** Guest self-order: straight to the kitchen; opens the table if needed (ADR-0029). */
    createGuestOrder: (
      token: string,
      body: In<typeof G.createGuestOrderRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/guest/tables/${e(token)}/orders`, guestOrderResponseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    createGuestPayment: (
      token: string,
      body: In<typeof G.createGuestPaymentRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/guest/tables/${e(token)}/payments`, guestOnlinePaymentSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getGuestPayment: (token: string, paymentId: string) =>
      request(
        'GET',
        `/v1/guest/tables/${e(token)}/payments/${e(paymentId)}`,
        guestOnlinePaymentSchema,
      ),
    getSimulatorCheckout: (reference: string) =>
      request('GET', `/v1/payment-simulator/${e(reference)}`, simulatorCheckoutSchema),
    completeSimulatorCheckout: (
      reference: string,
      body: In<typeof G.simulatorCompleteRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/payment-simulator/${e(reference)}/complete`, simulatorCheckoutSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Catalog & stations
    getCatalog: (branchId: string) =>
      request('GET', `${br(branchId)}/catalog`, catalogResponseSchema),
    createStation: (
      branchId: string,
      body: In<typeof C.createStationRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/kitchen/stations`, kitchenStationSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateStation: (branchId: string, id: string, body: In<typeof C.updateStationRequestSchema>) =>
      request('PATCH', `${br(branchId)}/kitchen/stations/${e(id)}`, kitchenStationSchema, { body }),
    createCategory: (
      branchId: string,
      body: In<typeof C.createCategoryRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/catalog/categories`, menuCategorySchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateCategory: (
      branchId: string,
      id: string,
      body: In<typeof C.updateCategoryRequestSchema>,
    ) =>
      request('PATCH', `${br(branchId)}/catalog/categories/${e(id)}`, menuCategorySchema, { body }),
    createMenuItem: (
      branchId: string,
      body: In<typeof C.createMenuItemRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/catalog/items`, menuItemSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateMenuItem: (
      branchId: string,
      id: string,
      body: In<typeof C.updateMenuItemRequestSchema>,
    ) => request('PATCH', `${br(branchId)}/catalog/items/${e(id)}`, menuItemSchema, { body }),
    createModifierGroup: (
      branchId: string,
      body: In<typeof C.upsertModifierGroupRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/catalog/modifier-groups`, modifierGroupSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateModifierGroup: (
      branchId: string,
      id: string,
      body: In<typeof C.upsertModifierGroupRequestSchema>,
    ) =>
      request('PUT', `${br(branchId)}/catalog/modifier-groups/${e(id)}`, modifierGroupSchema, {
        body,
      }),

    // ---- Orders
    listOrders: (
      branchId: string,
      query: { status?: 'open' | 'closed'; order_type?: 'dine_in' | 'takeaway' | 'delivery' } = {},
    ) => request('GET', `${br(branchId)}/orders`, orderListResponseSchema, { query }),
    getOrder: (branchId: string, orderId: string) =>
      request('GET', `${br(branchId)}/orders/${e(orderId)}`, orderSchema),
    /** Returns the session's existing (non-cancelled) order (200) or creates one (201). */
    createOrder: (
      branchId: string,
      body: In<typeof O.createOrderRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/orders`, orderSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    addOrderItem: (
      branchId: string,
      orderId: string,
      body: In<typeof O.addOrderItemRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/orders/${e(orderId)}/items`, orderSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateOrderItem: (
      branchId: string,
      orderId: string,
      itemId: string,
      body: In<typeof O.updateOrderItemRequestSchema>,
    ) =>
      request('PATCH', `${br(branchId)}/orders/${e(orderId)}/items/${e(itemId)}`, orderSchema, {
        body,
      }),
    cancelOrderItem: (
      branchId: string,
      orderId: string,
      itemId: string,
      body: In<typeof O.cancelOrderItemRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/orders/${e(orderId)}/items/${e(itemId)}/cancel`,
        orderSchema,
        {
          body,
          ...(idempotencyKey && { idempotencyKey }),
        },
      ),
    submitOrder: (
      branchId: string,
      orderId: string,
      body: In<typeof O.submitOrderRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/orders/${e(orderId)}/submit`, orderSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    serveOrderItems: (
      branchId: string,
      orderId: string,
      body: In<typeof O.serveOrderItemsRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/orders/${e(orderId)}/serve`, orderSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Kitchen
    listKitchenTickets: (branchId: string, query: In<typeof K.kitchenTicketListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/kitchen/tickets`, kitchenTicketListResponseSchema, {
        query: query as Record<string, string | undefined>,
      }),
    updateTicketStatus: (
      branchId: string,
      ticketId: string,
      body: In<typeof K.updateTicketStatusRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/kitchen/tickets/${e(ticketId)}/status`,
        kitchenTicketSchema,
        {
          body,
          ...(idempotencyKey && { idempotencyKey }),
        },
      ),

    // ---- Billing & cash
    listBills: (branchId: string, query: { status?: 'open' | 'settled' } = {}) =>
      request('GET', `${br(branchId)}/bills`, billListResponseSchema, { query }),
    getBill: (branchId: string, billId: string) =>
      request('GET', `${br(branchId)}/bills/${e(billId)}`, billSchema),
    createBill: (
      branchId: string,
      body: In<typeof B.createBillRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/bills`, billSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    createPayment: (
      branchId: string,
      body: In<typeof B.createPaymentRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/payments`, paymentResultSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getCurrentCashShift: (branchId: string) =>
      request('GET', `${br(branchId)}/cash-shifts/current`, currentCashShiftResponseSchema),
    listCashShifts: (branchId: string) =>
      request('GET', `${br(branchId)}/cash-shifts`, cashShiftListResponseSchema),
    openCashShift: (
      branchId: string,
      body: In<typeof B.openCashShiftRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/cash-shifts`, cashShiftSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    addCashMovement: (
      branchId: string,
      shiftId: string,
      body: In<typeof B.cashMovementRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/cash-shifts/${e(shiftId)}/movements`, cashShiftSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    closeCashShift: (
      branchId: string,
      shiftId: string,
      body: In<typeof B.closeCashShiftRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/cash-shifts/${e(shiftId)}/close`, cashShiftSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Reports
    getSalesReport: (branchId: string, query: In<typeof Rep.salesReportQuerySchema>) =>
      request('GET', `${br(branchId)}/reports/sales`, salesReportSchema, { query }),

    // ---- Inventory master data (tenant)
    listInventoryItems: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/inventory/items`, inventoryItemListResponseSchema),
    createInventoryItem: (
      tenantId: string,
      body: In<typeof Inv.createInventoryItemRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/tenants/${e(tenantId)}/inventory/items`, inventoryItemSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateInventoryItem: (
      tenantId: string,
      itemId: string,
      body: In<typeof Inv.updateInventoryItemRequestSchema>,
    ) =>
      request(
        'PATCH',
        `/v1/tenants/${e(tenantId)}/inventory/items/${e(itemId)}`,
        inventoryItemSchema,
        { body },
      ),
    createItemUnit: (
      tenantId: string,
      itemId: string,
      body: In<typeof Inv.createItemUnitRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `/v1/tenants/${e(tenantId)}/inventory/items/${e(itemId)}/units`,
        inventoryItemSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    updateItemUnit: (
      tenantId: string,
      itemId: string,
      unitId: string,
      body: In<typeof Inv.updateItemUnitRequestSchema>,
    ) =>
      request(
        'PATCH',
        `/v1/tenants/${e(tenantId)}/inventory/items/${e(itemId)}/units/${e(unitId)}`,
        inventoryItemSchema,
        { body },
      ),

    // ---- Inventory (branch)
    listStockLocations: (branchId: string) =>
      request('GET', `${br(branchId)}/inventory/locations`, stockLocationListResponseSchema),
    createStockLocation: (
      branchId: string,
      body: In<typeof Inv.createStockLocationRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/locations`, stockLocationSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateStockLocation: (
      branchId: string,
      locationId: string,
      body: In<typeof Inv.updateStockLocationRequestSchema>,
    ) =>
      request(
        'PATCH',
        `${br(branchId)}/inventory/locations/${e(locationId)}`,
        stockLocationSchema,
        {
          body,
        },
      ),
    getStock: (branchId: string, query: In<typeof Inv.stockQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/inventory/stock`, stockResponseSchema, { query }),
    listStockMovements: (
      branchId: string,
      query: Partial<Record<keyof In<typeof Inv.stockMovementQuerySchema>, string>> = {},
    ) => request('GET', `${br(branchId)}/inventory/movements`, stockMovementPageSchema, { query }),
    getValuation: (branchId: string, query: In<typeof Inv.valuationQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/inventory/valuation`, valuationResponseSchema, { query }),
    getValuationHistory: (branchId: string, query: In<typeof Inv.valuationHistoryQuerySchema>) =>
      request(
        'GET',
        `${br(branchId)}/inventory/valuation/history`,
        valuationHistoryResponseSchema,
        {
          query,
        },
      ),
    postOpeningBalance: (
      branchId: string,
      body: In<typeof Inv.openingBalanceRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/opening-balances`, postingResultSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Procurement: suppliers (tenant)
    listSuppliers: (tenantId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/procurement/suppliers`,
        supplierListResponseSchema,
      ),
    createSupplier: (
      tenantId: string,
      body: In<typeof Proc.createSupplierRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/tenants/${e(tenantId)}/procurement/suppliers`, supplierSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateSupplier: (
      tenantId: string,
      supplierId: string,
      body: In<typeof Proc.updateSupplierRequestSchema>,
    ) =>
      request(
        'PATCH',
        `/v1/tenants/${e(tenantId)}/procurement/suppliers/${e(supplierId)}`,
        supplierSchema,
        { body },
      ),

    // ---- Procurement: purchase orders (branch)
    listPurchaseOrders: (
      branchId: string,
      query: In<typeof Proc.purchaseOrderListQuerySchema> = {},
    ) =>
      request(
        'GET',
        `${br(branchId)}/procurement/purchase-orders`,
        purchaseOrderListResponseSchema,
        {
          query,
        },
      ),
    getPurchaseOrder: (branchId: string, poId: string) =>
      request('GET', `${br(branchId)}/procurement/purchase-orders/${e(poId)}`, purchaseOrderSchema),
    createPurchaseOrder: (
      branchId: string,
      body: In<typeof Proc.createPurchaseOrderRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/procurement/purchase-orders`, purchaseOrderSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updatePurchaseOrder: (
      branchId: string,
      poId: string,
      body: In<typeof Proc.updatePurchaseOrderRequestSchema>,
    ) =>
      request(
        'PATCH',
        `${br(branchId)}/procurement/purchase-orders/${e(poId)}`,
        purchaseOrderSchema,
        { body },
      ),
    approvePurchaseOrder: (
      branchId: string,
      poId: string,
      body: In<typeof Proc.purchaseOrderActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-orders/${e(poId)}/approve`,
        purchaseOrderSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    cancelPurchaseOrder: (
      branchId: string,
      poId: string,
      body: In<typeof Proc.purchaseOrderCloseRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-orders/${e(poId)}/cancel`,
        purchaseOrderSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    closePurchaseOrder: (
      branchId: string,
      poId: string,
      body: In<typeof Proc.purchaseOrderCloseRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-orders/${e(poId)}/close`,
        purchaseOrderSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Procurement: goods receipts (branch)
    listGoodsReceipts: (
      branchId: string,
      query: In<typeof Proc.goodsReceiptListQuerySchema> = {},
    ) =>
      request('GET', `${br(branchId)}/procurement/goods-receipts`, goodsReceiptListResponseSchema, {
        query,
      }),
    getGoodsReceipt: (branchId: string, receiptId: string) =>
      request(
        'GET',
        `${br(branchId)}/procurement/goods-receipts/${e(receiptId)}`,
        goodsReceiptSchema,
      ),
    createGoodsReceipt: (
      branchId: string,
      body: In<typeof Proc.createGoodsReceiptRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/procurement/goods-receipts`, goodsReceiptSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateGoodsReceipt: (
      branchId: string,
      receiptId: string,
      body: In<typeof Proc.updateGoodsReceiptRequestSchema>,
    ) =>
      request(
        'PATCH',
        `${br(branchId)}/procurement/goods-receipts/${e(receiptId)}`,
        goodsReceiptSchema,
        { body },
      ),
    cancelGoodsReceipt: (
      branchId: string,
      receiptId: string,
      body: In<typeof Proc.goodsReceiptActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/goods-receipts/${e(receiptId)}/cancel`,
        goodsReceiptSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    postGoodsReceipt: (
      branchId: string,
      receiptId: string,
      body: In<typeof Proc.goodsReceiptActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/goods-receipts/${e(receiptId)}/post`,
        goodsReceiptPostResultSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Lots, holds, expiry (branch)
    listLots: (
      branchId: string,
      query: Partial<Record<keyof In<typeof Inv.lotListQuerySchema>, string>> = {},
    ) => request('GET', `${br(branchId)}/inventory/lots`, lotListResponseSchema, { query }),
    getLot: (branchId: string, lotId: string) =>
      request('GET', `${br(branchId)}/inventory/lots/${e(lotId)}`, lotDetailSchema),
    holdLot: (
      branchId: string,
      lotId: string,
      body: In<typeof Inv.lotHoldRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/lots/${e(lotId)}/hold`, lotSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    releaseLot: (
      branchId: string,
      lotId: string,
      body: In<typeof Inv.lotHoldRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/lots/${e(lotId)}/release`, lotSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getExpiryAlerts: (branchId: string, query: { horizon_days?: number } = {}) =>
      request('GET', `${br(branchId)}/inventory/expiry-alerts`, expiryAlertResponseSchema, {
        query: {
          ...(query.horizon_days !== undefined && { horizon_days: String(query.horizon_days) }),
        },
      }),
    getFefo: (
      branchId: string,
      query: { item_id: string; location_id: string; qty_scaled?: number },
    ) =>
      request('GET', `${br(branchId)}/inventory/fefo`, fefoResponseSchema, {
        query: {
          item_id: query.item_id,
          location_id: query.location_id,
          ...(query.qty_scaled !== undefined && { qty_scaled: String(query.qty_scaled) }),
        },
      }),

    // ---- Waste (branch)
    listWaste: (
      branchId: string,
      query: Partial<Record<keyof In<typeof W.wasteListQuerySchema>, string>> = {},
    ) => request('GET', `${br(branchId)}/inventory/waste`, wasteListResponseSchema, { query }),
    getWaste: (branchId: string, wasteId: string) =>
      request('GET', `${br(branchId)}/inventory/waste/${e(wasteId)}`, wasteRecordSchema),
    createWaste: (
      branchId: string,
      body: In<typeof W.createWasteRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste`, wasteRecordSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateWaste: (branchId: string, wasteId: string, body: In<typeof W.updateWasteRequestSchema>) =>
      request('PATCH', `${br(branchId)}/inventory/waste/${e(wasteId)}`, wasteRecordSchema, {
        body,
      }),
    submitWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste/${e(wasteId)}/submit`, wasteRecordSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    approveWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste/${e(wasteId)}/approve`, wasteRecordSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    rejectWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste/${e(wasteId)}/reject`, wasteRecordSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    postWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste/${e(wasteId)}/post`, wastePostResultSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    reverseWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteReverseRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/inventory/waste/${e(wasteId)}/reverse`,
        wastePostResultSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    cancelWaste: (
      branchId: string,
      wasteId: string,
      body: In<typeof W.wasteActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/waste/${e(wasteId)}/cancel`, wasteRecordSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    /** Evidence photo or PDF (multipart field `file`, ≤ 5 MB, JPEG/PNG/WebP/PDF). */
    uploadWasteEvidence: (
      branchId: string,
      wasteId: string,
      file: Blob,
      idempotencyKey?: string,
    ) => {
      const form = new FormData();
      form.append('file', file);
      return upload(
        `${br(branchId)}/inventory/waste/${e(wasteId)}/evidence`,
        wasteRecordSchema,
        form,
        idempotencyKey,
      );
    },
    getWasteReport: (branchId: string, query: In<typeof W.wasteReportQuerySchema>) =>
      request('GET', `${br(branchId)}/inventory/waste-report`, wasteReportSchema, { query }),

    // ---- Stock counts (branch)
    listStockCounts: (branchId: string, query: In<typeof Cnt.stockCountListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/inventory/counts`, stockCountListResponseSchema, { query }),
    getStockCount: (branchId: string, countId: string) =>
      request('GET', `${br(branchId)}/inventory/counts/${e(countId)}`, stockCountSchema),
    createStockCount: (
      branchId: string,
      body: In<typeof Cnt.createStockCountRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    startStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/start`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    saveStockCountEntries: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountEntriesRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/lines`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    submitStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/submit`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    recountStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountRecountRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/recount`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    approveStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/approve`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    rejectStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/reject`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    postStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/inventory/counts/${e(countId)}/post`,
        stockCountPostResultSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    cancelStockCount: (
      branchId: string,
      countId: string,
      body: In<typeof Cnt.stockCountActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/counts/${e(countId)}/cancel`, stockCountSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Purchase order approval (P1.5)
    /** Draft → pending approval (or approved when no policy applies). */
    submitPurchaseOrder: (
      branchId: string,
      poId: string,
      body: In<typeof Proc.purchaseOrderActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-orders/${e(poId)}/submit`,
        purchaseOrderSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Purchase requests (branch)
    listPurchaseRequests: (
      branchId: string,
      query: In<typeof Proc.purchaseRequestListQuerySchema> = {},
    ) =>
      request(
        'GET',
        `${br(branchId)}/procurement/purchase-requests`,
        purchaseRequestListResponseSchema,
        { query },
      ),
    getPurchaseRequest: (branchId: string, prId: string) =>
      request(
        'GET',
        `${br(branchId)}/procurement/purchase-requests/${e(prId)}`,
        purchaseRequestSchema,
      ),
    createPurchaseRequest: (
      branchId: string,
      body: In<typeof Proc.createPurchaseRequestRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/procurement/purchase-requests`, purchaseRequestSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updatePurchaseRequest: (
      branchId: string,
      prId: string,
      body: In<typeof Proc.updatePurchaseRequestRequestSchema>,
    ) =>
      request(
        'PATCH',
        `${br(branchId)}/procurement/purchase-requests/${e(prId)}`,
        purchaseRequestSchema,
        { body },
      ),
    submitPurchaseRequest: (
      branchId: string,
      prId: string,
      body: In<typeof Proc.purchaseRequestActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-requests/${e(prId)}/submit`,
        purchaseRequestSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    cancelPurchaseRequest: (
      branchId: string,
      prId: string,
      body: In<typeof Proc.purchaseRequestCancelRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-requests/${e(prId)}/cancel`,
        purchaseRequestSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    /** Approved request → draft purchase order (returns the new PO). */
    convertPurchaseRequest: (
      branchId: string,
      prId: string,
      body: In<typeof Proc.convertPurchaseRequestRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/purchase-requests/${e(prId)}/convert`,
        purchaseOrderSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Approvals (branch)
    getApprovalInbox: (branchId: string) =>
      request('GET', `${br(branchId)}/approvals/inbox`, inboxResponseSchema),
    listApprovals: (branchId: string, query: In<typeof Appr.approvalListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/approvals`, approvalRequestListResponseSchema, { query }),
    getApproval: (branchId: string, requestId: string) =>
      request('GET', `${br(branchId)}/approvals/${e(requestId)}`, approvalRequestSchema),
    approveApproval: (
      branchId: string,
      requestId: string,
      body: In<typeof Appr.approvalDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/approvals/${e(requestId)}/approve`, approvalRequestSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    /** A rejection needs a note. */
    rejectApproval: (
      branchId: string,
      requestId: string,
      body: In<typeof Appr.approvalDecisionRequestSchema> & { note: string },
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/approvals/${e(requestId)}/reject`, approvalRequestSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    escalateApproval: (
      branchId: string,
      requestId: string,
      body: In<typeof Appr.approvalEscalateRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/approvals/${e(requestId)}/escalate`, approvalRequestSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Approval policies and delegations (tenant)
    listApprovalPolicies: (tenantId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/approvals/policies`,
        approvalPolicyListResponseSchema,
      ),
    createApprovalPolicy: (
      tenantId: string,
      body: In<typeof Appr.createApprovalPolicyRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `/v1/tenants/${e(tenantId)}/approvals/policies`, approvalPolicySchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateApprovalPolicy: (
      tenantId: string,
      policyId: string,
      body: In<typeof Appr.updateApprovalPolicyRequestSchema>,
    ) =>
      request(
        'PATCH',
        `/v1/tenants/${e(tenantId)}/approvals/policies/${e(policyId)}`,
        approvalPolicySchema,
        { body },
      ),
    listApprovalDelegations: (tenantId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/approvals/delegations`,
        approvalDelegationListResponseSchema,
      ),
    createApprovalDelegation: (
      tenantId: string,
      body: In<typeof Appr.createApprovalDelegationRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `/v1/tenants/${e(tenantId)}/approvals/delegations`,
        approvalDelegationSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    revokeApprovalDelegation: (tenantId: string, delegationId: string, idempotencyKey?: string) =>
      request(
        'POST',
        `/v1/tenants/${e(tenantId)}/approvals/delegations/${e(delegationId)}/revoke`,
        z.object({ id: z.string(), revoked_at: z.string() }),
        { body: {}, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Budgets (branch)
    listBudgets: (branchId: string, query: In<typeof Bud.budgetListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/finance/budgets`, budgetListResponseSchema, { query }),
    getBudgetEntries: (branchId: string, budgetId: string) =>
      request(
        'GET',
        `${br(branchId)}/finance/budgets/${e(budgetId)}/entries`,
        budgetEntriesResponseSchema,
      ),
    createBudget: (
      branchId: string,
      body: In<typeof Bud.createBudgetRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/budgets`, budgetSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    /** On an approved budget, a new amount opens a revision that needs approval. */
    updateBudget: (
      branchId: string,
      budgetId: string,
      body: In<typeof Bud.updateBudgetRequestSchema>,
    ) => request('PATCH', `${br(branchId)}/finance/budgets/${e(budgetId)}`, budgetSchema, { body }),
    approveBudget: (
      branchId: string,
      budgetId: string,
      body: In<typeof Bud.budgetDecisionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/budgets/${e(budgetId)}/approve`, budgetSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    rejectBudget: (
      branchId: string,
      budgetId: string,
      body: In<typeof Bud.budgetDecisionRequestSchema> & { note: string },
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/budgets/${e(budgetId)}/reject`, budgetSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Approval helpers (pickers)
    /** Active tenant members (excluding the caller) for the delegation picker. */
    listApprovalDelegates: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/approvals/delegates`, memberListResponseSchema),
    /** Members who may take the current step when escalated (approval managers). */
    listApprovalAssignable: (branchId: string, requestId: string) =>
      request(
        'GET',
        `${br(branchId)}/approvals/${e(requestId)}/assignable`,
        memberListResponseSchema,
      ),

    // ---- Accounts payable (tenant settings)
    getApSettings: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/finance/ap/settings`, apSettingsSchema),
    updateApSettings: (tenantId: string, body: In<typeof Ap.updateApSettingsRequestSchema>) =>
      request('PATCH', `/v1/tenants/${e(tenantId)}/finance/ap/settings`, apSettingsSchema, {
        body,
      }),

    // ---- Accounts payable: invoices (branch)
    listInvoices: (branchId: string, query: QueryOf<typeof Ap.invoiceListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/finance/ap/invoices`, invoiceListResponseSchema, {
        query,
      }),
    getInvoice: (branchId: string, invoiceId: string) =>
      request('GET', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}`, invoiceSchema),
    createInvoice: (
      branchId: string,
      body: In<typeof Ap.createInvoiceRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.updateInvoiceRequestSchema>,
    ) =>
      request('PATCH', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}`, invoiceSchema, {
        body,
      }),
    matchInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/match`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    submitInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/submit`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    postInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/post`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    disputeInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceReasonRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/dispute`,
        invoiceSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    reopenInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/reopen`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    voidInvoice: (
      branchId: string,
      invoiceId: string,
      body: In<typeof Ap.invoiceReasonRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/invoices/${e(invoiceId)}/void`, invoiceSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),

    // ---- Accounts payable: credit notes
    listCreditNotes: (branchId: string, query: Record<string, string | undefined> = {}) =>
      request('GET', `${br(branchId)}/finance/ap/credit-notes`, creditNoteListResponseSchema, {
        query,
      }),
    createCreditNote: (
      branchId: string,
      body: In<typeof Ap.createCreditNoteRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/credit-notes`, creditNoteSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    postCreditNote: (
      branchId: string,
      creditId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/finance/ap/credit-notes/${e(creditId)}/post`,
        creditNoteSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    voidCreditNote: (
      branchId: string,
      creditId: string,
      body: In<typeof Ap.invoiceActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/finance/ap/credit-notes/${e(creditId)}/void`,
        creditNoteSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Accounts payable: payments
    listApPayments: (branchId: string, query: QueryOf<typeof Ap.apPaymentListQuerySchema> = {}) =>
      request('GET', `${br(branchId)}/finance/ap/payments`, apPaymentListResponseSchema, {
        query,
      }),
    createApPayment: (
      branchId: string,
      body: In<typeof Ap.createApPaymentRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/finance/ap/payments`, apPaymentSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    verifyApPayment: (
      branchId: string,
      paymentId: string,
      body: In<typeof Ap.apPaymentActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/finance/ap/payments/${e(paymentId)}/verify`,
        apPaymentSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    rejectApPayment: (
      branchId: string,
      paymentId: string,
      body: In<typeof Ap.apPaymentRejectRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/finance/ap/payments/${e(paymentId)}/reject`,
        apPaymentSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    getApAging: (branchId: string, query: { as_of?: string } = {}) =>
      request('GET', `${br(branchId)}/finance/ap/aging`, agingResponseSchema, { query }),

    // ---- Stock transfers (branch)
    listStockTransfers: (
      branchId: string,
      query: QueryOf<typeof Tr.stockTransferListQuerySchema> = {},
    ) =>
      request('GET', `${br(branchId)}/inventory/transfers`, stockTransferListResponseSchema, {
        query,
      }),
    getStockTransfer: (branchId: string, transferId: string) =>
      request('GET', `${br(branchId)}/inventory/transfers/${e(transferId)}`, stockTransferSchema),
    createStockTransfer: (
      branchId: string,
      body: In<typeof Tr.createStockTransferRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/transfers`, stockTransferSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    dispatchStockTransfer: (
      branchId: string,
      transferId: string,
      body: In<typeof Tr.stockTransferActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/inventory/transfers/${e(transferId)}/dispatch`,
        stockTransferPostResultSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    /** Called on the DESTINATION branch. */
    receiveStockTransfer: (
      branchId: string,
      transferId: string,
      body: In<typeof Tr.stockTransferActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/inventory/transfers/${e(transferId)}/receive`,
        stockTransferPostResultSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),
    cancelStockTransfer: (
      branchId: string,
      transferId: string,
      body: In<typeof Tr.stockTransferActionRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/inventory/transfers/${e(transferId)}/cancel`,
        stockTransferSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Recipes and consumption (branch)
    listRecipes: (branchId: string) =>
      request('GET', `${br(branchId)}/recipes`, recipeListResponseSchema),
    createRecipe: (
      branchId: string,
      body: In<typeof Rcp.createRecipeRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/recipes`, recipeSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    updateRecipe: (
      branchId: string,
      recipeId: string,
      body: In<typeof Rcp.updateRecipeRequestSchema>,
    ) => request('PATCH', `${br(branchId)}/recipes/${e(recipeId)}`, recipeSchema, { body }),
    /** Processes pending sales consumption now (normally done by the jobs worker). */
    runConsumption: (branchId: string, idempotencyKey?: string) =>
      request('POST', `${br(branchId)}/inventory/consumption/run`, consumptionRunResultSchema, {
        body: {},
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getConsumptionReport: (
      branchId: string,
      query: QueryOf<typeof Rcp.consumptionReportQuerySchema>,
    ) =>
      request('GET', `${br(branchId)}/inventory/consumption`, consumptionReportSchema, {
        query,
      }),

    // ---- Supplier analytics (branch)
    getSupplierScorecard: (
      branchId: string,
      supplierId: string,
      query: QueryOf<typeof SA.analyticsWindowQuerySchema> = {},
    ) =>
      request(
        'GET',
        `${br(branchId)}/procurement/suppliers/${e(supplierId)}/scorecard`,
        scorecardSchema,
        { query },
      ),
    getSupplierPriceHistory: (
      branchId: string,
      supplierId: string,
      query: QueryOf<typeof SA.priceHistoryQuerySchema> = {},
    ) =>
      request(
        'GET',
        `${br(branchId)}/procurement/suppliers/${e(supplierId)}/price-history`,
        priceHistoryResponseSchema,
        { query },
      ),
    compareSuppliers: (branchId: string, query: QueryOf<typeof SA.supplierCompareQuerySchema>) =>
      request(
        'GET',
        `${br(branchId)}/procurement/suppliers/compare`,
        supplierCompareResponseSchema,
        { query },
      ),
    createSupplierRating: (
      branchId: string,
      supplierId: string,
      body: In<typeof SA.createSupplierRatingRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `${br(branchId)}/procurement/suppliers/${e(supplierId)}/ratings`,
        supplierRatingSchema,
        { body, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Recalls and reconciliation (branch)
    searchRecall: (branchId: string, query: QueryOf<typeof Rcl.recallSearchQuerySchema>) =>
      request('GET', `${br(branchId)}/inventory/recall-search`, recallSearchResponseSchema, {
        query,
      }),
    listRecallCases: (branchId: string) =>
      request('GET', `${br(branchId)}/inventory/recalls`, recallCaseListResponseSchema),
    getRecallCase: (branchId: string, caseId: string) =>
      request('GET', `${br(branchId)}/inventory/recalls/${e(caseId)}`, recallCaseSchema),
    createRecallCase: (
      branchId: string,
      body: In<typeof Rcl.createRecallCaseRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/recalls`, recallCaseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    closeRecallCase: (
      branchId: string,
      caseId: string,
      body: In<typeof Rcl.closeRecallCaseRequestSchema>,
      idempotencyKey?: string,
    ) =>
      request('POST', `${br(branchId)}/inventory/recalls/${e(caseId)}/close`, recallCaseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    /** Stock accuracy across posted counts (P1 §10), with its denominator. */
    getCountAccuracy: (branchId: string, query: { from?: string; to?: string } = {}) =>
      request('GET', `${br(branchId)}/inventory/count-accuracy`, countAccuracyResponseSchema, {
        query,
      }),
    getReconciliation: (branchId: string) =>
      request('GET', `${br(branchId)}/inventory/reconciliation`, reconciliationReportSchema),

    // ---- Platform console (ADR-0032) and the tenant side of support access (D8)
    platformMe: () => request('GET', '/v1/platform/me', Pf.platformMeSchema),
    listPlatformTenants: (query: Pf.PlatformTenantListQuery = {}) =>
      request('GET', '/v1/platform/tenants', Pf.platformTenantListSchema, { query }),
    getPlatformTenant: (tenantId: string) =>
      request('GET', `/v1/platform/tenants/${e(tenantId)}`, Pf.platformTenantDetailSchema),
    createPlatformTenant: (body: Pf.CreateTenantRequest, idempotencyKey?: string) =>
      request('POST', '/v1/platform/tenants', Pf.createTenantResponseSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    suspendTenant: (tenantId: string, body: In<typeof Pf.suspendTenantRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/tenants/${e(tenantId)}/suspend`,
        Pf.tenantActionResponseSchema,
        { body },
      ),
    reactivateTenant: (tenantId: string, body: In<typeof Pf.reactivateTenantRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/tenants/${e(tenantId)}/reactivate`,
        Pf.tenantActionResponseSchema,
        { body },
      ),
    extendTenantTrial: (tenantId: string, body: In<typeof Pf.extendTrialRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/tenants/${e(tenantId)}/extend-trial`,
        Pf.tenantActionResponseSchema,
        { body },
      ),
    changeTenantPlan: (tenantId: string, body: In<typeof Pf.changePlanRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/tenants/${e(tenantId)}/change-plan`,
        Pf.tenantActionResponseSchema,
        { body },
      ),
    requestSupportAccess: (tenantId: string, body: Pf.RequestSupportAccess) =>
      request('POST', `/v1/platform/tenants/${e(tenantId)}/support-access`, Pf.supportGrantSchema, {
        body,
      }),
    getSupportOverview: (tenantId: string) =>
      request(
        'GET',
        `/v1/platform/tenants/${e(tenantId)}/support/overview`,
        Pf.supportOverviewSchema,
      ),
    listPlans: () => request('GET', '/v1/platform/plans', Pf.planListSchema),
    updatePlan: (code: string, body: Pf.UpdatePlanRequest) =>
      request('PATCH', `/v1/platform/plans/${e(code)}`, Pf.planSchema, { body }),
    getBillingSettings: () =>
      request('GET', '/v1/platform/settings/billing', Pf.billingSettingResponseSchema),
    updateBillingSettings: (body: In<typeof Pf.updateBillingSettingsRequestSchema>) =>
      request('PATCH', '/v1/platform/settings/billing', Pf.billingSettingResponseSchema, { body }),
    getLegalSettings: () =>
      request('GET', '/v1/platform/settings/legal', Pf.legalSettingResponseSchema),
    updateLegalSettings: (body: In<typeof Pf.updateLegalSettingsRequestSchema>) =>
      request('PATCH', '/v1/platform/settings/legal', Pf.legalSettingResponseSchema, { body }),
    listPlatformAdmins: () => request('GET', '/v1/platform/admins', Pf.platformAdminListSchema),
    addPlatformAdmin: (body: In<typeof Pf.addPlatformAdminRequestSchema>) =>
      request('POST', '/v1/platform/admins', Pf.platformAdminSchema, { body }),
    removePlatformAdmin: (userId: string) =>
      request('DELETE', `/v1/platform/admins/${e(userId)}`, z.object({ removed: z.string() })),
    listPlatformAudit: (query: { tenant_id?: string; cursor?: string } = {}) =>
      request('GET', '/v1/platform/audit', Pf.platformAuditPageSchema, { query }),
    listSupportGrants: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/support-access`, Pf.supportGrantListSchema),
    decideSupportGrant: (
      tenantId: string,
      grantId: string,
      decision: 'approve' | 'reject' | 'revoke',
      idempotencyKey?: string,
    ) =>
      request(
        'POST',
        `/v1/tenants/${e(tenantId)}/support-access/${e(grantId)}/${decision}`,
        Pf.supportGrantSchema,
        { body: {}, ...(idempotencyKey && { idempotencyKey }) },
      ),

    // ---- Data lifecycle (ADR-0034): export, cancellation, platform restore/purge
    listTenantExports: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/exports`, DL.tenantExportListSchema),
    requestTenantExport: (tenantId: string, idempotencyKey?: string) =>
      request('POST', `/v1/tenants/${e(tenantId)}/exports`, DL.tenantExportSchema, {
        body: {},
        ...(idempotencyKey && { idempotencyKey }),
      }),
    getExportDownloadLink: (tenantId: string, exportId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/exports/${e(exportId)}/download`,
        DL.exportDownloadLinkSchema,
      ),
    getDataOverview: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/data`, DL.dataOverviewSchema),
    cancelTenant: (tenantId: string, body: DL.CancelTenantRequest, idempotencyKey?: string) =>
      request('POST', `/v1/tenants/${e(tenantId)}/data/cancel`, DL.dataOverviewSchema, {
        body,
        ...(idempotencyKey && { idempotencyKey }),
      }),
    listLifecycleTenants: () =>
      request('GET', '/v1/platform/lifecycle/tenants', DL.platformLifecycleTenantListSchema),
    restoreTenant: (tenantId: string, body: In<typeof DL.restoreTenantRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/lifecycle/tenants/${e(tenantId)}/restore`,
        DL.platformLifecycleTenantSchema,
        { body },
      ),
    purgeTenantNow: (tenantId: string, body: In<typeof DL.purgeTenantRequestSchema>) =>
      request(
        'POST',
        `/v1/platform/lifecycle/tenants/${e(tenantId)}/purge-now`,
        DL.purgeTenantResponseSchema,
        { body },
      ),
    getRetentionPolicy: () =>
      request('GET', '/v1/platform/lifecycle/retention', DL.retentionPolicySchema),
    updateRetentionPolicy: (body: In<typeof DL.updateRetentionPolicyRequestSchema>) =>
      request('PATCH', '/v1/platform/lifecycle/retention', DL.retentionPolicySchema, { body }),

    // ---- Subscription billing (ADR-0033): tenant billing page and platform invoices
    getSubscription: (tenantId: string) =>
      request('GET', `/v1/tenants/${e(tenantId)}/subscription`, Sub.subscriptionOverviewSchema),
    listSubscriptionInvoices: (tenantId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/subscription/invoices`,
        Sub.subscriptionInvoiceListSchema,
      ),
    getSubscriptionInvoice: (tenantId: string, invoiceId: string) =>
      request(
        'GET',
        `/v1/tenants/${e(tenantId)}/subscription/invoices/${e(invoiceId)}`,
        Sub.subscriptionInvoiceDetailSchema,
      ),
    listPlatformInvoices: (query: QueryOf<typeof Sub.platformInvoiceListQuerySchema> = {}) =>
      request('GET', '/v1/platform/billing/invoices', Sub.platformSubscriptionInvoiceListSchema, {
        query,
      }),
    issuePlatformInvoice: (body: Sub.IssueSubscriptionInvoiceRequest) =>
      request('POST', '/v1/platform/billing/invoices', Sub.platformSubscriptionInvoiceSchema, {
        body,
      }),
    recordSubscriptionPayment: (body: Sub.RecordSubscriptionPaymentRequest) =>
      request('POST', '/v1/platform/billing/payments', Sub.subscriptionPaymentResultSchema, {
        body,
      }),
    voidPlatformInvoice: (invoiceId: string, body: Sub.VoidSubscriptionInvoiceRequest) =>
      request(
        'POST',
        `/v1/platform/billing/invoices/${e(invoiceId)}/void`,
        Sub.platformSubscriptionInvoiceSchema,
        { body },
      ),
    runBillingCycle: (body: In<typeof Sub.runBillingCycleRequestSchema> = {}) =>
      request('POST', '/v1/platform/billing/cycle', Sub.billingCycleResultSchema, { body }),

    /** WebSocket URL for branch realtime hints (same origin; cookie-authenticated). */
    realtimeUrl: (branchId: string, origin: string) =>
      `${(baseUrl || origin).replace(/^http/, 'ws')}${br(branchId)}/realtime`,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
